import { styled, Typography } from '@mui/material';
import type { ReactNode } from 'react';

type Props = {
  /** Visible caption naming the code (also its accessible name). */
  label: string;
  code: string;
  /** Control next to the caption, typically a copy button. */
  action?: ReactNode;
  'data-testid'?: string;
};

const MAX_HEIGHT = 280;
const MONOSPACE = '"SFMono-Regular", "Menlo", "Consolas", "Liberation Mono", monospace';

const Figure = styled('figure')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  margin: 0,
}));

const Caption = styled('figcaption')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

const Pre = styled('pre')(({ theme }) => ({
  margin: 0,
  maxHeight: MAX_HEIGHT,
  overflow: 'auto',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.grey[50],
  fontFamily: MONOSPACE,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

/** Read-only code or script the user must copy elsewhere. */
export function CodeBlock(props: Props) {
  return (
    <Figure data-testid={props['data-testid'] ?? 'code-block'}>
      <Caption>
        <Typography variant="subtitle2" component="span">
          {props.label}
        </Typography>
        {props.action}
      </Caption>
      <Pre tabIndex={0} aria-label={props.label}>
        <code>{props.code}</code>
      </Pre>
    </Figure>
  );
}

export type { Props as CodeBlockProps };
